import { NavLink, useNavigate } from "react-router-dom";
import VersionBadge from "@/components/VersionBadge";
import { useAuth, can } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { useI18n } from "@/contexts/I18nContext";
import {
  LayoutDashboard, Building2, ArrowLeftRight, BellRing,
  FileBarChart2, Users2, ScrollText, LogOut, Moon, Sun, Languages, Menu, X,
  Wrench, UserSquare2, Calculator,
} from "lucide-react";
import { NAV, AUTH } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { useState } from "react";

function NavItem({ to, icon: Icon, label, testId }) {
  return (
    <NavLink
      to={to}
      data-testid={testId}
      className={({ isActive }) =>
        `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ` +
        (isActive
          ? "bg-secondary text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60")
      }
    >
      <Icon className="w-[18px] h-[18px]" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const { lang, t, toggle: toggleLang } = useI18n();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const sidebar = (
    <aside
      data-testid={NAV.sidebar}
      className="h-full w-64 shrink-0 border-r border-border/60 bg-card/60 flex flex-col overflow-hidden"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="px-5 py-6 border-b border-border/60 shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md bg-foreground text-background grid place-items-center font-bold">P</div>
          <div>
            <div className="text-sm font-heading font-semibold tracking-tight">{t("app.name")}</div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">{t("app.tagline")}</div>
          </div>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
        <NavItem to="/" icon={LayoutDashboard} label={t("nav.dashboard")} testId={NAV.linkDashboard} />
        <NavItem to="/properties" icon={Building2} label={t("nav.properties")} testId={NAV.linkProperties} />
        <NavItem to="/tenants" icon={UserSquare2} label={t("nav.tenants")} testId="nav-link-tenants" />
        <NavItem to="/maintenance" icon={Wrench} label={t("nav.maintenance")} testId="nav-link-maintenance" />
        <NavItem to="/transactions" icon={ArrowLeftRight} label={t("nav.transactions")} testId={NAV.linkTransactions} />
        <NavItem to="/simulator" icon={Calculator} label="Simulador" testId="nav-link-simulator" />
        <NavItem to="/alerts" icon={BellRing} label={t("nav.alerts")} testId={NAV.linkAlerts} />
        <NavItem to="/reports" icon={FileBarChart2} label={t("nav.reports")} testId={NAV.linkReports} />
        {can(user, "manageUsers") && (
          <NavItem to="/users" icon={Users2} label={t("nav.users")} testId={NAV.linkUsers} />
        )}
        {can(user, "viewAudit") && (
          <NavItem to="/audit" icon={ScrollText} label={t("nav.audit")} testId={NAV.linkAudit} />
        )}
      </nav>

      <div className="px-3 py-4 border-t border-border/60 space-y-2 shrink-0">
        <div className="flex items-center gap-2 px-2 pb-2">
          <div className="w-8 h-8 rounded-full bg-secondary grid place-items-center text-xs font-semibold">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm truncate font-medium">{user?.name}</div>
            <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user?.role}</div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid={AUTH.themeToggle}
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={toggleTheme}
          >
            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button
            data-testid={AUTH.languageToggle}
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={toggleLang}
          >
            <Languages className="w-4 h-4 mr-1" /> {lang.toUpperCase()}
          </Button>
        </div>
        <Button
          data-testid={AUTH.logoutBtn}
          variant="ghost"
          className="w-full justify-start text-muted-foreground hover:text-foreground"
          onClick={handleLogout}
        >
          <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
        </Button>
        <VersionBadge />
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <div className="hidden lg:flex sticky top-0 h-screen">{sidebar}</div>

      {/* Mobile sidebar */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full">{sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header
          className="glass sticky top-0 z-30 border-b border-border/60 flex items-center gap-3 px-4 lg:px-8"
          style={{
            paddingTop: "max(env(safe-area-inset-top), 0.25rem)",
            paddingLeft: "max(env(safe-area-inset-left), 1rem)",
            paddingRight: "max(env(safe-area-inset-right), 1rem)",
            minHeight: "calc(3.5rem + env(safe-area-inset-top))",
          }}
        >
          <Button
            variant="outline"
            size="icon"
            className="lg:hidden"
            data-testid={NAV.mobileToggle}
            onClick={() => setMobileOpen((v) => !v)}
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <div className="text-sm text-muted-foreground">
            <span className="hidden sm:inline">{t("app.name")}</span>
          </div>
        </header>
        <main
          className="flex-1 px-4 lg:px-8 py-6 lg:py-8"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom), 1.5rem)" }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
